import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import EsimCard from "./component/EsimCard";
import TopupModal from "./component/TopupModal";
import "./EsimPage.css";
import "./../loader/loader.css";
import { useLanguage } from "../i18n/LanguageContext";

const IcActive = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M5 20V10M12 20V4M19 20v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
);
const IcArchive = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="3" y="4" width="18" height="5" rx="1.5" stroke="currentColor" strokeWidth="2" />
        <path d="M5 9v9a2 2 0 002 2h10a2 2 0 002-2V9M10 13h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
);
const IcPlusW = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="9" stroke="#fff" strokeWidth="2" />
        <path d="M12 8v8M8 12h8" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
    </svg>
);
const IcChevron = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <polyline points="6 9 12 15 18 9" stroke="#595959" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

// Big illustrative tile used by the empty / not-logged-in states.
const StateTile = ({ icon, title, desc, children }) => (
    <div className="ms-state">
        <div className="ms-state-icon">{icon}</div>
        <h3 className="ms-state-title">{title}</h3>
        <p className="ms-state-desc">{desc}</p>
        {children}
    </div>
);

const SimGlyph = () => (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M5 4a2 2 0 012-2h7l5 5v13a2 2 0 01-2 2H7a2 2 0 01-2-2z" stroke="#237804" strokeWidth="1.8" strokeLinejoin="round" />
        <rect x="9" y="12" width="6" height="6" rx="1.5" stroke="#237804" strokeWidth="1.8" />
    </svg>
);
const LoginGlyph = () => (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M14 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4" stroke="#237804" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M10 8l-4 4 4 4M6 12h9" stroke="#237804" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const SkeletonCard = () => (
    <div className="ms-card ms-card-skel" aria-hidden="true">
        <div className="ms-card-top">
            <span className="ms-skel ms-skel-thumb" />
            <span className="ms-skel ms-skel-name" />
            <span className="ms-skel ms-skel-status" />
        </div>
        <div className="ms-card-meta">
            <span className="ms-skel ms-skel-chip" />
            <span className="ms-skel ms-skel-chip ms-skel-chip-sm" />
        </div>
        <div className="ms-card-actions">
            <span className="ms-skel ms-skel-btn" />
            <span className="ms-skel ms-skel-btn" />
        </div>
    </div>
);

const LoadingSkeleton = () => {
    const { t } = useLanguage();
    return (
    <div className="ms-page">
        <div className="ms-content" aria-busy="true" aria-label={t("esims.loadingAria")}>
            <h1 className="ms-title">{t("esims.title")}</h1>
            <div className="ms-tabs">
                <span className="ms-skel ms-skel-tab" />
                <span className="ms-skel ms-skel-tab" />
            </div>
            <div className="ms-listbar">
                <span className="ms-skel ms-skel-text" />
                <span className="ms-skel ms-skel-sort" />
            </div>
            <div className="ms-card-list">
                <SkeletonCard />
                <SkeletonCard />
            </div>
        </div>
    </div>
    );
};

const EsimPage = () => {
    const { isAuthenticated, user, isLoading, loginWithRedirect } = useAuth0();
    const navigate = useNavigate();
    const location = useLocation();
    const { pathname, search } = location;
    const { t } = useLanguage();

    const [esimList, setEsimList] = useState([]);
    const [loadingEsims, setLoadingEsims] = useState(false);
    const [hasFetched, setHasFetched] = useState(false); // first /esims fetch resolved?
    const [esimError, setEsimError] = useState(false);
    const [activeTab, setActiveTab] = useState("active"); // "active" | "archived"
    const [sortOrder, setSortOrder] = useState("newest"); // "newest" | "oldest"
    const [showSort, setShowSort] = useState(false);

    // Top-up modal state
    const [showTopUpModal, setShowTopUpModal] = useState(false);
    const [topUpOptions, setTopUpOptions] = useState([]);
    const [modalLoading, setModalLoading] = useState(false);
    const [selectedEsimIccid, setSelectedEsimIccid] = useState(null);
    const [selectedSimId, setSelectedSimId] = useState(null);

    const openTopUpModal = async (SimId, iccid) => {
        setSelectedSimId(SimId);
        setSelectedEsimIccid(iccid);
        setShowTopUpModal(true);
        setModalLoading(true);
        try {
            const res = await fetch(`https://api.artamobile.id/v1/product/topup/${SimId}`);
            if (!res.ok) throw new Error("Failed to load top-up options");
            const data = await res.json();
            setTopUpOptions(data.productList || []);
        } catch (err) {
            console.error("Failed to load top-up options", err);
            setTopUpOptions([]);
        } finally {
            setModalLoading(false);
        }
    };

    useEffect(() => {
        if (!isAuthenticated || !user?.email) return;
        const controller = new AbortController();
        const fetchEsims = async () => {
            setLoadingEsims(true);
            setEsimError(false);
            try {
                const res = await fetch("https://api.artamobile.id/v1/esims", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ email: user.email }),
                    signal: controller.signal,
                });
                if (!res.ok) throw new Error("Failed to fetch eSIMs");
                const data = await res.json();
                setEsimList(data.esimList || []);
            } catch (err) {
                if (err.name === "AbortError") return;
                console.error("Error fetching eSIMs:", err);
                setEsimError(true);
            } finally {
                // Only settle if this request wasn't aborted (unmount / re-fetch);
                // otherwise leave the skeleton up for the request that supersedes it.
                if (!controller.signal.aborted) {
                    setLoadingEsims(false);
                    setHasFetched(true);
                }
            }
        };
        fetchEsims();
        return () => controller.abort();
    }, [isAuthenticated, user?.email]);

    // Keep the skeleton until auth resolves and (for signed-in users) the first
    // /esims fetch completes — avoids flashing the empty state before data loads.
    if (isLoading || loadingEsims || (isAuthenticated && !hasFetched)) {
        return <LoadingSkeleton />;
    }

    const buyNewBtn = (
        <button className="ms-buy-btn" onClick={() => navigate(`/${search}`)}>
            <IcPlusW /> {t("esims.buyNew")}
        </button>
    );

    // Not logged in
    if (!isAuthenticated) {
        return (
            <div className="ms-page">
                <div className="ms-content ms-content-centered">
                    <StateTile
                        icon={<LoginGlyph />}
                        title={t("esims.loginTitle")}
                        desc={t("esims.loginDesc")}
                    >
                        <button
                            className="ms-buy-btn"
                            onClick={() => {
                                localStorage.setItem("redirectAfterLogin", `${pathname}${search}`);
                                loginWithRedirect().catch((e) => console.error("Login error:", e));
                            }}
                        >
                            {t("common.login")}
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <path d="M5 12h14M13 6l6 6-6 6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                    </StateTile>
                </div>
            </div>
        );
    }

    const activeEsims = esimList.filter((e) => !e.archive);
    const archivedEsims = esimList.filter((e) => e.archive);
    const list = activeTab === "active" ? activeEsims : archivedEsims;
    const sorted = list.slice().sort((a, b) => {
        const diff = new Date(b.createdAt) - new Date(a.createdAt);
        return sortOrder === "newest" ? diff : -diff;
    });

    return (
        <div className="ms-page">
            <div className="ms-content">
                <h1 className="ms-title">{t("esims.title")}</h1>

                {/* Tabs */}
                <div className="ms-tabs" role="tablist">
                    <button
                        role="tab"
                        aria-selected={activeTab === "active"}
                        className={`ms-tab ${activeTab === "active" ? "active" : ""}`}
                        onClick={() => setActiveTab("active")}
                    >
                        <IcActive /> {t("esims.active")}
                        <span className="ms-tab-count">{activeEsims.length}</span>
                    </button>
                    <button
                        role="tab"
                        aria-selected={activeTab === "archived"}
                        className={`ms-tab ${activeTab === "archived" ? "active" : ""}`}
                        onClick={() => setActiveTab("archived")}
                    >
                        <IcArchive /> {t("esims.archived")}
                        <span className="ms-tab-count">{archivedEsims.length}</span>
                    </button>
                </div>

                {esimError ? (
                    <p className="ms-error">{t("esims.loadError")}</p>
                ) : (
                    <>
                        {/* Count + sort */}
                        {sorted.length > 0 && (
                            <div className="ms-listbar">
                                <span className="ms-count">{t("esims.count", { n: list.length })}</span>
                                <div className="ms-sort">
                                    <button className="ms-sort-btn" onClick={() => setShowSort((s) => !s)}>
                                        {sortOrder === "newest" ? t("esims.newest") : t("esims.oldest")} <IcChevron />
                                    </button>
                                    {showSort && (
                                        <div className="ms-sort-menu">
                                            <button
                                                className={sortOrder === "newest" ? "active" : ""}
                                                onClick={() => { setSortOrder("newest"); setShowSort(false); }}
                                            >{t("esims.newest")}</button>
                                            <button
                                                className={sortOrder === "oldest" ? "active" : ""}
                                                onClick={() => { setSortOrder("oldest"); setShowSort(false); }}
                                            >{t("esims.oldest")}</button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* List or empty state */}
                        {sorted.length > 0 ? (
                            <div className="ms-card-list">
                                {sorted.map((item, idx) => (
                                    <EsimCard key={item.iccid || idx} item={item} openTopUpModal={openTopUpModal} />
                                ))}
                            </div>
                        ) : activeTab === "active" ? (
                            <StateTile
                                icon={<SimGlyph />}
                                title={t("esims.emptyActiveTitle")}
                                desc={t("esims.emptyActiveDesc")}
                            />
                        ) : (
                            <StateTile
                                icon={<IcArchive />}
                                title={t("esims.emptyArchiveTitle")}
                                desc={t("esims.emptyArchiveDesc")}
                            />
                        )}
                    </>
                )}

                {buyNewBtn}
            </div>

            <TopupModal
                isOpen={showTopUpModal}
                onClose={() => setShowTopUpModal(false)}
                loading={modalLoading}
                options={topUpOptions}
                onChoose={(opt) => {
                    if (!selectedSimId) { alert(t("common.genericError")); return; }
                    const params = new URLSearchParams(location.search);
                    params.set('topup', '1');
                    params.set('iccid', selectedEsimIccid);
                    params.set('SimId', selectedSimId);
                    navigate(`/product/${opt.code}?${params.toString()}`);
                }}
                title={t("esims.topupTitle")}
                subtitle={t("esims.topupSubtitle")}
            />
        </div>
    );
};

export default EsimPage;
